import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm } from './client'

/** IT Solutions - `Easeedesk::it_solutions` si capetele lui. */

export interface SolutionSubcategory {
  ID: number
  subcategory_name: string
}

export interface SolutionCategory {
  id: number
  name: string
  /** Ce se mai poate adauga aici; goala = butonul "Add solution" e stins. */
  subcategories: SolutionSubcategory[]
}

export interface SolutionsPage {
  title: string
  companyId: number
  /** `$_SESSION['access'] == 3`: butonul "Master Edit". */
  masterEdit: boolean
  categories: SolutionCategory[]
  levelColors: Record<string, string>
}

export interface SolutionAttachment {
  /** `it_solutions_attachments.ID` - pentru "Attach to ticket". */
  id: number
  file_name: string
  is_image: boolean
  /** Gol cand instanta n-are server de descarcari sau cheie. */
  url: string
  thumbnail: string
}

export interface SolutionEntry {
  ID: number
  subcategory_id: number
  subcategory_name: string
  level_name: string
  level_color: string
  priority: string
  technology: string
  description: string
  attachments: SolutionAttachment[]
}

export interface SolutionEntries {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: SolutionEntry[]
}

export const solutionsPageQuery = queryOptions({
  queryKey: ['easeedesk', 'it-solutions'],
  queryFn: () => apiGet<SolutionsPage>('/easeedesk/it-solutions'),
})

export function solutionEntriesQuery(category: number, search: string, start: number, length: number, orderDir: 'asc' | 'desc') {
  const p = new URLSearchParams({
    start: String(start),
    length: String(length),
    order_column: '0',
    order_dir: orderDir,
  })
  if (search) p.set('search', search)
  return queryOptions({
    queryKey: ['easeedesk', 'it-solutions', category, search, start, length, orderDir],
    queryFn: () => apiGet<SolutionEntries>(`/easeedesk/it-solutions/${category}/entries?${p}`),
  })
}

export function levelDescriptionQuery(subcategory: number, level: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'it-solutions', 'level', subcategory, level],
    queryFn: () =>
      apiGet<{ level: string; title: string; description: string }>(
        `/easeedesk/it-solutions/level?subcategory=${subcategory}&level=${level}`
      ),
  })
}

export function addSolution(categoryId: number, subcategoryId: number, technology: string, description: string) {
  return apiPost<{ ok: boolean; id?: number; message?: string }>('/easeedesk/it-solutions/add', {
    category_id: categoryId,
    subcategory_id: subcategoryId,
    technology,
    description,
  })
}

export function editSolution(entry: number, level: string, technology: string, description: string) {
  return apiPost<{ ok: boolean; message?: string }>(`/easeedesk/it-solutions/${entry}/edit`, {
    level_name: level,
    technology,
    description,
  })
}

export function removeSolution(entry: number) {
  return apiPost<{ ok: boolean; message?: string }>(`/easeedesk/it-solutions/${entry}/remove`, {})
}

/* -------------------------------------------------------------------------
 * BIBLIOTECA FURNIZORULUI, NOTELE SI FISIERELE - `Solutions::categories`,
 * `create_category` .. `delete_subcategory`, `feedback`, `rate`,
 * `addAttachment`, `get_solutions_attachments`.
 * ---------------------------------------------------------------------- */

export interface SolutionsAdminCategory {
  ID: number
  category_name: string
  status: number
  solutions_number: number
  /** Rolurile care o vad (cerinta 46); goala = toata lumea. */
  roles: number[]
}

export interface SolutionsAdminSubcategory {
  ID: number
  subcategory_name: string
  priority: number
  status: number
  /** Cele patru descrieri de nivel, pe `level_name` 0..3. */
  levels: Record<string, string>
  /** Cine vede articolul; vine numai pentru cine administreaza biblioteca. */
  access?: DocumentAccess
}

/** Un rol sau un om de bifat pe un document. */
export interface AccessOption {
  id: number
  name: string
}

/** Regula unui document; amandoua goale = mosteneste categoria. */
export interface DocumentAccess {
  roles: number[]
  users: number[]
}

export const INHERIT_ACCESS: DocumentAccess = { roles: [], users: [] }

/**
 * Campurile regulii in POST. `access` e semnul ca regula vine in cerere: un
 * formular nu trimite o lista goala, si fara semn "nimic bifat" nu s-ar putea
 * deosebi de "nu schimba".
 */
export function accessFields(access: DocumentAccess): Record<string, string | string[]> {
  return { access: '1', access_roles: access.roles.map(String), access_users: access.users.map(String) }
}

export interface SolutionsAdminPage {
  canEdit: boolean
  categories: SolutionsAdminCategory[]
  roles: { id: number; name: string }[]
  /** Oamenii de bifat pe un articol - gol pentru cine doar citeste. */
  people?: AccessOption[]
}

export const solutionsAdminQuery = queryOptions({
  queryKey: ['easeedesk', 'it-solutions', 'admin'],
  queryFn: () => apiGet<SolutionsAdminPage>('/easeedesk/it-solutions/categories'),
})

export function solutionsAdminSubcategoriesQuery(category: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'it-solutions', 'admin', category, 'subcategories'],
    queryFn: () => apiGet<{ category: number; subcategories: SolutionsAdminSubcategory[] }>(`/easeedesk/it-solutions/categories/${category}/subcategories`),
    enabled: category > 0,
  })
}

type Ok = { ok: boolean; id?: number; message?: string }

export function createSolutionsCategory(name: string, roles: number[]) {
  return apiPost<Ok>('/easeedesk/it-solutions/categories', { category_name: name, roles: roles.map(String) })
}
export function editSolutionsCategory(category: number, name: string, roles: number[]) {
  return apiPost<Ok>(`/easeedesk/it-solutions/categories/${category}/edit`, { category_name: name, roles: roles.map(String) })
}
export function activateSolutionsCategory(category: number, on: boolean) {
  return apiPost<Ok>(`/easeedesk/it-solutions/categories/${category}/activate`, { status: on ? 'true' : 'false' })
}
export function removeSolutionsCategory(category: number) {
  return apiPost<Ok>(`/easeedesk/it-solutions/categories/${category}/remove`, {})
}

export interface SubcategoryForm {
  subcategory_name: string
  priority: number
  level_0: string
  level_1: string
  level_2: string
  level_3: string
}

export function createSolutionsSubcategory(category: number, form: SubcategoryForm, access?: DocumentAccess) {
  return apiPost<Ok>(`/easeedesk/it-solutions/categories/${category}/subcategories`, { ...form, priority: String(form.priority), ...(access ? accessFields(access) : {}) })
}
export function editSolutionsSubcategory(subcategory: number, form: SubcategoryForm, access?: DocumentAccess) {
  return apiPost<Ok>(`/easeedesk/it-solutions/subcategories/${subcategory}/edit`, { ...form, priority: String(form.priority), ...(access ? accessFields(access) : {}) })
}
export function activateSolutionsSubcategory(subcategory: number, on: boolean) {
  return apiPost<Ok>(`/easeedesk/it-solutions/subcategories/${subcategory}/activate`, { status: on ? 'true' : 'false' })
}
export function removeSolutionsSubcategory(subcategory: number) {
  return apiPost<Ok>(`/easeedesk/it-solutions/subcategories/${subcategory}/remove`, {})
}

/** Notele, oricare ar fi lucrul notat - solutie sau articol Self-Service. */
export interface FeedbackMarks {
  storage: boolean
  summary: { avg: number; count: number }
  mine: { ID: number; rating: number; comment: string } | null
  entries: { id: number; rating: number; comment: string; created: string; ticket: number; author: string }[]
}

export interface SolutionFeedbackPage extends FeedbackMarks {
  subcategory: number
}

export function solutionFeedbackQuery(subcategory: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'it-solutions', 'feedback', subcategory],
    queryFn: () => apiGet<SolutionFeedbackPage>(`/easeedesk/it-solutions/feedback/${subcategory}`),
  })
}

export function rateSolution(subcategory: number, rating: number, comment: string, ticket: number) {
  return apiPost<{ status: string; message: string }>('/easeedesk/it-solutions/feedback', {
    subcategory: String(subcategory),
    rating: String(rating),
    comment,
    ticket: String(ticket),
  })
}

/**
 * Notele unui articol Self-Service: acelasi tabel si acelasi capat de scriere
 * ca la solutii, cu `article` in loc de `subcategory`.
 */
export interface ArticleFeedbackPage extends FeedbackMarks {
  article: number
}

export function articleFeedbackQuery(article: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'it-solutions', 'feedback', 'article', article],
    queryFn: () => apiGet<ArticleFeedbackPage>(`/easeedesk/it-solutions/feedback/article/${article}`),
  })
}

export function rateArticle(article: number, rating: number, comment: string) {
  return apiPost<{ status: string; message: string }>('/easeedesk/it-solutions/feedback', {
    article: String(article),
    rating: String(rating),
    comment,
  })
}

/** `filesToUpload[]`, multipart, ca `#formUpload` din ferestrele vechi. */
export function attachSolutionFile(entry: number, files: File[]) {
  const body = new FormData()
  for (const file of files) body.append('filesToUpload[]', file, file.name)

  return apiPostForm<{ status: string; message: string }>(`/easeedesk/it-solutions/${entry}/attachments`, body)
}
